import { useTill } from "@/lib/till-context";
import { addMonths, date, dateTime, fmtSerial, money, personName } from "@/lib/format";
import { METHOD_LABEL, type Sale, type ZReport } from "@/lib/types";
import { tierTag } from "@/lib/pricing";

/** 80 mm receipt, printed through the browser (works with any printer the device knows). */
export function ReceiptPrint({ sale }: { sale: Sale; tillName?: string }) {
  const { store, me } = useTill();
  const lineDisc = sale.lines.reduce((n, l) => n + l.discount, 0);
  return (
    <div className="print-only">
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{store?.storeName ?? "Store"}</div>
        {me?.settings.receiptHeader ? <div style={{ whiteSpace: "pre-line" }}>{me.settings.receiptHeader}</div> : null}
      </div>
      <Hr />
      <Row a="Receipt" b={sale.number} />
      <Row a="Date" b={dateTime(sale.createdAt)} />
      <Row a="Served by" b={personName(sale.staffName)} />
      {sale.customerName || sale.customerPhone ? <Row a="Customer" b={sale.customerName ?? sale.customerPhone} /> : null}
      {sale.pricingTier && sale.pricingTier !== "retail" ? <Row a="Prices" b="Wholesale" /> : null}
      <Hr />
      {sale.lines.map((l, i) => (
        <div key={i} style={{ marginBottom: 4 }}>
          <div>{l.name}</div>
          <Row a={`${l.qty} x ${money(l.price)}`} b={money(l.price * l.qty - l.discount)} />
          {l.discount ? <div>Discount -{money(l.discount)}</div> : null}
          {l.serials.map((sn) => (
            <div key={sn}>
              {"  "}IMEI/SN {fmtSerial(sn)}
              {l.warrantyMonths ? ` · warranty to ${date(addMonths(sale.createdAt, l.warrantyMonths))}` : ""}
            </div>
          ))}
        </div>
      ))}
      <Hr />
      <Row a="Subtotal" b={money(sale.subtotal - lineDisc)} />
      {sale.discount ? <Row a={`Discount${sale.discountNote ? ` (${sale.discountNote})` : ""}`} b={`-${money(sale.discount)}`} /> : null}
      {(() => {
        const saved = sale.lines.reduce((n, l) => n + (tierTag(l.tier) && l.listPrice != null ? (l.listPrice - l.price) * l.qty : 0), 0);
        return saved > 0 ? <Row a="You saved" b={money(saved)} /> : null;
      })()}
      <Row a={<b>TOTAL</b>} b={<b>{money(sale.total)}</b>} />
      {sale.payments.map((p, i) => (
        <div key={i}>
          <Row a={`Paid by ${METHOD_LABEL[p.method]}${p.last4 ? ` ****${p.last4}` : ""}`} b={money(p.tendered ?? p.amount)} />
          {p.change ? <Row a="Change" b={money(p.change)} /> : null}
        </div>
      ))}
      <Hr />
      {me?.settings.receiptFooter ? <div style={{ textAlign: "center", whiteSpace: "pre-line" }}>{me.settings.receiptFooter}</div> : null}
      <div style={{ textAlign: "center", marginTop: 6 }}>Thank you!</div>
    </div>
  );
}

export function ZPrint({ z }: { z: ZReport }) {
  const { store } = useTill();
  const s = z.summary;
  const out = s ? s.cashRefunds + s.drops + s.paidOut : 0;
  const diff = z.difference ?? 0;
  const wholesale = s?.byTier ? { count: s.byTier.wholesale.count + s.byTier.walkin.count, amount: s.byTier.wholesale.amount + s.byTier.walkin.amount } : null;
  return (
    <div className="print-only">
      <div style={{ textAlign: "center", fontWeight: 700, fontSize: 15 }}>{store?.storeName ?? "Store"}</div>
      <div style={{ textAlign: "center", fontWeight: 700 }}>SHIFT REPORT</div>
      <div style={{ textAlign: "center" }}>{z.number}</div>
      <Hr />
      <Row a="Till" b={z.tillName ?? "Till"} />
      <Row a="Teller" b={personName(z.staffName)} />
      <Row a="Started" b={dateTime(z.openedAt)} />
      {z.closedAt ? <Row a="Finished" b={dateTime(z.closedAt)} /> : null}
      {s ? (
        <>
          <Heading>Sales</Heading>
          <Row a={<b>Total sales ({s.sales})</b>} b={<b>{money(s.takings)}</b>} />
          {Object.entries(s.byMethod).map(([m, v]) => (
            <Row key={m} a={`${METHOD_LABEL[m as keyof typeof METHOD_LABEL] ?? m} (${v.count})`} b={money(v.amount)} />
          ))}
          {wholesale?.count ? <Row a={`Of which wholesale (${wholesale.count})`} b={money(wholesale.amount)} /> : null}
          {s.discounts ? <Row a="Discounts given" b={money(s.discounts)} /> : null}
          {s.refunds ? <Row a={`Refunds (${s.refunds})`} b={money(s.refundTotal)} /> : null}
          {s.devices ? <Row a="Devices sold" b={String(s.devices)} /> : null}
          {s.deliveries && (s.deliveries.deliver || s.deliveries.collect) ? <Row a="Deliveries and collections" b={String(s.deliveries.deliver + s.deliveries.collect)} /> : null}
          <Heading>Cash in the drawer</Heading>
          <Row a="Starting cash" b={money(s.openingFloat)} />
          <Row a="Cash sales" b={money(s.cashSales)} />
          {s.paidIn ? <Row a="Cash added" b={money(s.paidIn)} /> : null}
          {out ? <Row a="Cash taken out" b={`-${money(out)}`} /> : null}
        </>
      ) : null}
      <Hr />
      <Row a={<b>Should be in the drawer</b>} b={<b>{money(z.expected)}</b>} />
      <Row a="Counted" b={money(z.counted)} />
      <Row a={<b>{diff === 0 ? "Cash is exact" : diff < 0 ? "Short by" : "Over by"}</b>} b={diff === 0 ? "" : <b>{money(Math.abs(diff))}</b>} />
      {z.note ? <div style={{ marginTop: 4 }}>Note: {z.note}</div> : null}
      {z.closeReason ? <div style={{ marginTop: 4, fontWeight: 700 }}>Counted without the teller: {z.closeReason}</div> : null}
      {z.closedBy && z.closeReason ? <div>Closed by: {personName(z.closedBy)}</div> : null}
      {z.approvedBy ? <div style={{ marginTop: 4 }}>Confirmed by: {personName(z.approvedBy)}</div> : null}
      <div style={{ marginTop: 18 }}>Signed: ______________________</div>
    </div>
  );
}

const Heading = ({ children }: { children: React.ReactNode }) => (
  <>
    <Hr />
    <div style={{ fontWeight: 700, marginBottom: 2 }}>{children}</div>
  </>
);

const Hr = () => <div style={{ borderTop: "1px dashed #000", margin: "5px 0" }} />;
const Row = ({ a, b }: { a: React.ReactNode; b: React.ReactNode }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
    <span>{a}</span>
    <span>{b}</span>
  </div>
);
